<script setup>
import { computed, onMounted, ref, toRaw, watch } from 'vue'
import BarnDocumentation from '@/components/incidents/BarnDocumentation/BarnDocumentation.vue'
import BarnIntervention from '@/components/incidents/BarnIntervention.vue'
import GenericIncident from '@/components/incidents/GenericIncident.vue'
import DeadAnimalIncident from '@/components/incidents/DeadAnimalIncident.vue'
import TriggerIncident from '@/components/incidentTriggers/TriggerIncident.vue'
import TimeSeriesIncident from '@/components/incidents/TimeSeriesIncident.vue'
import { useInstanceStore } from '@/store/backendInstances'
import PenState from '@/components/incidents/PenState.vue'
import { useIncidentStore } from '@/store/incident'

const instanceStore = useInstanceStore()
const incidentStore = useIncidentStore()
import i18n from '../../plugins/i18n'

const { mobile } = useDisplay()
const { t } = i18n.global
import { useToast } from 'vue-toastification'
import IncidentAssignButton from '@/components/IncidentAssignButton.vue'
import IncidentCommentButton from '@/components/incidents/IncidentCommentButton.vue'
import { useDisplay } from 'vuetify'
import ShareButton from '@/components/ShareButton.vue'
import HealthCheck from '@/components/incidents/HealthCheck.vue'
import LLMSummaryIncident from '@/components/incidents/LLMSummaryIncident.vue'
import HideShowButtons from '@/components/incidents/HideShowButtons.vue'
import { getOrigin, useProductName } from '@/composables/version.js'
import IncidentOptionalOverrideForm from '@/components/incidentTriggers/IncidentOptionalOverrideForm.vue'
import { humanReadableTimestamp } from '@/composables/datetime'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'
import { writeToClipboard } from '@/composables/clipboard'

const toast = useToast()
const props = defineProps({
  incidentId: { type: String, default: null },
  incident: { type: Object, default: null },
  showMoreButton: { type: Boolean, default: true },
  showTriggerButton: { type: Boolean, default: true },
  canTriggerNewEventOverride: { type: Function, default: undefined },
  hideJsonButton: { type: Boolean, default: false },
})
const emit = defineEmits(['reload'])

const showTriggerDialog = ref(false)
const closeIncidentDialog = ref(false)
const reopenIncidentDialog = ref(false)
const deleteIncidentDialog = ref(false)
const updateGenericPropertiesDialog = ref(false)
const forceClose = ref(false)
const loading = ref(false)
const selfLoadedIncident = ref(null)
const genericIncidentProperties = ref(null)
const showInfoDialog = ref(false)

const incidentToUse = computed(() => {
  return props.incident ? props.incident : selfLoadedIncident.value
})

const shareUrl = computed(() => {
  if (!incidentToUse.value) return null
  return `${getOrigin()}/instance/${instanceStore.selectedInstanceId}/incident/${incidentToUse.value.incidentId}`
})

const canHideAndShowIncident = computed(() => {
  if (!incidentToUse.value) return null
  return instanceStore.hasAnyPermission(['incidents.manage', 'incidents.manage.advanced', `incidents.${incidentToUse.value.type}.manage`])
})

const incidentComponent = computed(() => {
  if (!incidentToUse.value) return null
  switch (incidentToUse.value.type) {
    case 'barnDocumentation':
      return BarnDocumentation
    case 'barnIntervention':
      return BarnIntervention
    case 'deadAnimal':
      return DeadAnimalIncident
    case 'penState':
      return PenState
    case 'healthCheck':
      return HealthCheck
    case 'timeSeries':
      return TimeSeriesIncident
    case 'llmSummary':
      return LLMSummaryIncident
    default:
      return GenericIncident
  }
})

const canTriggerNewEvent = computed(() => {
  if (!incidentToUse.value) return false
  if (props.canTriggerNewEventOverride) return props.canTriggerNewEventOverride(props)
  if (!instanceStore.hasAnyPermission(['incidents.manage', 'incidents.manage.advanced', `incidents.${incidentToUse.value.type}.manage`])) return false
  return incidentToUse.value.state !== 'closed'
})

const canDeleteIncident = computed(() => {
  return instanceStore.hasPermission('incidents.manage.advanced')
})

const canUpdateGenericProperties = computed(() => {
  if (!incidentToUse.value) return false
  return instanceStore.hasAnyPermission(['incidents.manage', 'incidents.manage.advanced', `incidents.${incidentToUse.value.type}.manage`])
})

const canCloseIncident = computed(() => {
  if (!incidentToUse.value) return false
  if (!instanceStore.hasAnyPermission(['incidents.manage', 'incidents.manage.advanced', `incidents.${incidentToUse.value.type}.manage`])) return false
  return incidentToUse.value.state !== 'closed'
})

const canForceCloseIncident = computed(() => {
  if (!canCloseIncident.value) return false
  return instanceStore.hasPermission('incidents.manage.advanced')
})

const canReopenIncident = computed(() => {
  if (!incidentToUse.value) return false
  if (!instanceStore.hasAnyPermission(['incidents.manage', 'incidents.manage.advanced', `incidents.${incidentToUse.value.type}.manage`])) return false
  return incidentToUse.value.state === 'closed'
})

const incidentTitle = computed(() => {
  return `${t('incidents.type.' + incidentToUse.value.type)}, ${humanReadableTimestamp(incidentToUse.value.firstEventTimestamp)}`
})

const prettyJson = computed(() => {
  return JSON.stringify(incidentToUse.value, null, 2)
})

async function closeIncident() {
  if (!incidentToUse.value) return
  loading.value = true
  const closeResult = await incidentStore.closeIncident(incidentToUse.value.incidentId, forceClose.value)
  loading.value = false
  if (closeResult === null) {
    toast.error(t('toast.incident.close.error'))
  } else {
    toast.success(t('toast.incident.close.success'))
    closeIncidentDialog.value = false
    emit('reload')
  }
}

async function reopenIncident() {
  if (!incidentToUse.value) return
  loading.value = true
  const reopenResult = await incidentStore.reopenIncident(incidentToUse.value.incidentId)
  loading.value = false
  if (reopenResult === null) {
    toast.error(t('toast.incident.reopen.error'))
  } else {
    toast.success(t('toast.incident.reopen.success'))
    reopenIncidentDialog.value = false
    emit('reload')
  }
}

async function deleteIncident() {
  if (!incidentToUse.value) return
  loading.value = true
  const deleteResult = await incidentStore.deleteIncident(incidentToUse.value.incidentId)
  loading.value = false
  if (deleteResult === null) {
    toast.error(t('toast.incident.delete.error'))
  } else {
    toast.success(t('toast.incident.delete.success'))
    deleteIncidentDialog.value = false
    emit('reload')
  }
}

async function saveGenericProperties() {
  if (!incidentToUse.value) return
  loading.value = true
  const result = await incidentStore.updateGenericProperties(incidentToUse.value.incidentId, genericIncidentProperties.value)
  loading.value = false
  if (result === null) {
    toast.error(t('incidents.toast.update_generic_properties.error'))
  } else {
    toast.success(t('incidents.toast.update_generic_properties.success'))
    updateGenericPropertiesDialog.value = false
    emit('reload')
  }
}

function resetGenericIncidentProperties() {
  genericIncidentProperties.value = {
    displayConfiguration: incidentToUse.value.displayConfiguration,
    stakeholder: incidentToUse.value.stakeholder,
  }
}

async function copyJSON() {
  const result = await writeToClipboard(JSON.stringify(toRaw(incidentToUse.value), null, 2))
  if (!result) {
    toast.error(t('toast.copy_failed'))
    return
  }
  toast.success(t('toast.copied_to_clipboard'))
}

watch(
  incidentToUse,
  newValue => {
    if (!newValue) return
    resetGenericIncidentProperties()
  },
  { immediate: true, deep: true }
)

onMounted(async () => {
  if (!props.incident && props.incidentId) {
    selfLoadedIncident.value = await incidentStore.getIncident(props.incidentId)
  }
})
</script>

<template>
  <component
    :is="incidentComponent"
    v-if="incidentToUse"
    :incident="incidentToUse"
  >
    <template
      v-if="!hideJsonButton"
      #jsonButton
    >
      <v-btn
        class="rounded-circle"
        variant="text"
        size="small"
        icon
      >
        <v-icon icon="mdi-code-json" />
        <GenericCustomDialog
          v-model="showInfoDialog"
          activator="parent"
          max-width="800px"
          width="100%"
          card-class="pt-0"
          header-wrapper-class="pt-3 position-sticky top-0 w-100 bg-surface"
          hide-close-button
        >
          <template #headerActions>
            <div class="pa-2 d-flex justify-end ga-2">
              <v-btn
                class="rounded-circle"
                color="primary"
                icon
                variant="flat"
                size="x-small"
                @click="copyJSON"
              >
                <v-icon
                  icon="mdi-content-copy"
                  size="large"
                />
                <v-tooltip
                  activator="parent"
                  location="top"
                >
                  {{ $t('general_interface.buttons.copy') }}
                </v-tooltip>
              </v-btn>

              <v-btn
                class="rounded-circle"
                color="error"
                icon
                variant="flat"
                size="x-small"
                @click="showInfoDialog = false"
              >
                <v-icon
                  icon="mdi-close"
                  size="large"
                />
                <v-tooltip
                  activator="parent"
                  location="top"
                >
                  {{ $t('general_interface.buttons.close') }}
                </v-tooltip>
              </v-btn>
            </div>
          </template>
          <template #headerIcon>
            <v-icon
              icon="mdi-code-json"
              start
            />
          </template>
          <template #title>
            {{ incidentTitle }}
          </template>
          <template #body>
            <div>
              <pre style="overflow-x: auto"><code>{{ prettyJson }}</code></pre>
            </div>
          </template>
        </GenericCustomDialog>
      </v-btn>
    </template>
    <template #commentButton>
      <IncidentCommentButton :incident="incidentToUse" />
    </template>
    <template #assignButton>
      <IncidentAssignButton
        :incident="incidentToUse"
        @reload="$emit('reload')"
      />
    </template>
    <template #genericActions="{ enableNewEventButton, newEventButtonText, disableManagementButtons, enableHideAndShowButtons }">
      <HideShowButtons
        v-if="enableHideAndShowButtons && canHideAndShowIncident"
        :incident="incidentToUse"
        @update-incident="emit('reload')"
      />

      <ShareButton
        :url="shareUrl"
        color="success"
        variant="tonal"
        :text="$t('incidents.share_text', { appName: useProductName(true) })"
      />

      <v-btn
        v-if="canTriggerNewEvent && showTriggerButton && enableNewEventButton"
        prepend-icon="mdi-plus"
        variant="tonal"
        color="primary"
      >
        {{ newEventButtonText }}
        <v-dialog
          v-model="showTriggerDialog"
          activator="parent"
          width="auto"
          :fullscreen="mobile"
        >
          <TriggerIncident
            v-model="showTriggerDialog"
            :incident="incidentToUse"
            :show-cancel-button="true"
            @saved="
              () => {
                showTriggerDialog = false
                $emit('reload')
              }
            "
            @cancel="showTriggerDialog = false"
          />
        </v-dialog>
      </v-btn>

      <v-btn
        v-if="!disableManagementButtons && (canDeleteIncident || canCloseIncident || canUpdateGenericProperties)"
        variant="outlined"
      >
        {{ $t('general_interface.buttons.manage') }}
        <v-menu
          activator="parent"
          :close-on-content-click="false"
        >
          <v-list>
            <v-list-item
              v-if="canUpdateGenericProperties"
              :title="$t('assign_incidents.update_generic_properties')"
              value="update-generic-properties"
              @click="updateGenericPropertiesDialog = true"
            />

            <v-list-item
              v-if="canCloseIncident"
              prepend-icon="mdi-close-circle"
              base-color="warning"
              :title="$t('incidents.managing.close.button')"
              value="close"
              @click="closeIncidentDialog = true"
            />

            <v-list-item
              v-if="canReopenIncident"
              prepend-icon="mdi-undo"
              base-color="info"
              :title="$t('incidents.managing.reopen.button')"
              value="reopen"
              @click="reopenIncidentDialog = true"
            />

            <v-list-item
              v-if="canDeleteIncident"
              prepend-icon="mdi-delete-forever"
              base-color="error"
              variant="tonal"
              :title="$t('incidents.managing.delete.button')"
              value="delete"
              @click="deleteIncidentDialog = true"
            />
          </v-list>
        </v-menu>
      </v-btn>

      <v-btn
        v-if="showMoreButton"
        append-icon="mdi-chevron-right"
        variant="outlined"
        @click="$router.push({ name: 'IncidentView', params: { incidentId: incidentToUse.incidentId } })"
      >
        {{ $t('general_interface.buttons.more') }}
      </v-btn>

      <!-- Update Generic Properties Dialog -->
      <GenericCustomDialog
        v-model="updateGenericPropertiesDialog"
        max-width="800px"
        hide-cancel-button
        :card-props="{ loading: loading }"
      >
        <template #headerIcon>
          <v-icon icon="mdi-pencil" />
        </template>
        <template #title>
          {{ t('incidents.managing.update_generic_properties') }}
        </template>
        <template #body>
          <IncidentOptionalOverrideForm v-model="genericIncidentProperties" />
        </template>
        <template #actions>
          <v-btn
            prepend-icon="mdi-close"
            variant="outlined"
            @click="
              () => {
                updateGenericPropertiesDialog = false
                resetGenericIncidentProperties()
              }
            "
          >
            {{ $t('general_interface.buttons.cancel') }}
          </v-btn>
          <v-btn
            prepend-icon="mdi-check"
            variant="flat"
            color="primary"
            @click="saveGenericProperties"
          >
            {{ $t('general_interface.buttons.save') }}
          </v-btn>
        </template>
      </GenericCustomDialog>

      <!-- Close Incident Dialog -->
      <GenericCustomDialog
        v-model="closeIncidentDialog"
        max-width="800px"
        :card-props="{ loading: loading }"
      >
        <template #headerIcon>
          <v-icon icon="mdi-close-circle" />
        </template>
        <template #title>
          {{ $t('incidents.managing.close.title') }}
        </template>
        <template #body>
          <v-alert type="warning">
            {{ $t('incidents.managing.close.text') }}
          </v-alert>

          <v-checkbox
            v-if="canForceCloseIncident"
            v-model="forceClose"
            :label="$t('incidents.managing.close.force_close')"
            hide-details
          />
        </template>
        <template #actions>
          <v-btn
            prepend-icon="mdi-close-circle"
            color="warning"
            variant="flat"
            @click="closeIncident"
          >
            {{ $t('incidents.managing.close.button') }}
          </v-btn>
        </template>
      </GenericCustomDialog>

      <!-- Re-open Incident Dialog -->
      <GenericCustomDialog
        v-model="reopenIncidentDialog"
        max-width="800px"
        :card-props="{ loading: loading }"
      >
        <template #headerIcon>
          <v-icon icon="mdi-undo" />
        </template>
        <template #title>
          {{ $t('incidents.managing.reopen.title') }}
        </template>
        <template #body>
          <v-alert type="info">
            {{ $t('incidents.managing.reopen.text') }}
          </v-alert>
        </template>
        <template #actions>
          <v-btn
            prepend-icon="mdi-undo"
            color="info"
            variant="flat"
            @click="reopenIncident"
          >
            {{ $t('incidents.managing.reopen.button') }}
          </v-btn>
        </template>
      </GenericCustomDialog>

      <!-- Delete Incident Dialog -->
      <GenericCustomDialog
        v-model="deleteIncidentDialog"
        max-width="800px"
        :card-props="{ loading: loading }"
      >
        <template #headerIcon>
          <v-icon icon="mdi-delete-forever" />
        </template>
        <template #title>
          {{ $t('incidents.managing.delete.title') }}
        </template>
        <template #body>
          <v-alert type="error">
            {{ $t('incidents.managing.delete.text') }}
          </v-alert>
        </template>
        <template #actions>
          <v-btn
            prepend-icon="mdi-delete-forever"
            color="error"
            variant="flat"
            @click="deleteIncident"
          >
            {{ $t('incidents.managing.delete.button') }}
          </v-btn>
        </template>
      </GenericCustomDialog>
    </template>
  </component>
</template>
